import { injectLocal, provideLocal } from "@vueuse/core";
import { computed, ref, watchEffect, type InjectionKey } from "vue";
import { useQueue } from "../use-queue";

type ActivationDependentAction = () => Promise<void> | void;
enum ActivationQueueStatus {
  /**
   * No actions are currently queued
   */
  Empty,

  /**
   * Actions are queued and processing 1 by 1
   */
  Processing,

  /**
   * Actions are queued but waiting for the next user interaction to proceed
   */
  AwaitingInteraction,
}

const activationContainerInjectionKey = Symbol(
  "ActivationContainer",
) as InjectionKey<ReturnType<typeof useProvideActivationContainer>>;

export function useProvideActivationContainer() {
  const actionsQueue = useQueue<ActivationDependentAction>();
  const isAwaitingInteraction = ref(false);
  const queueTimeout = ref<number | undefined>(undefined);

  const status = computed<ActivationQueueStatus>({
    get: () => {
      if (actionsQueue.isEmpty.value) {
        return ActivationQueueStatus.Empty;
      }

      if (isAwaitingInteraction.value) {
        return ActivationQueueStatus.AwaitingInteraction;
      }

      return ActivationQueueStatus.Processing;
    },
    set: (value) => {
      switch (value) {
        case ActivationQueueStatus.Empty:
          throw new Error(
            "Cannot clear actions queue manually, wait until the actions finish processing",
          );
        case ActivationQueueStatus.AwaitingInteraction:
          isAwaitingInteraction.value = true;
          break;
        case ActivationQueueStatus.Processing:
          break;
      }
    },
  });

  function iterateQueue() {
    queueTimeout.value = window.setTimeout(async () => {
      const action = actionsQueue.peek();

      if (!action) return;
      try {
        try {
          await action();
        } catch (error) {
          // If the action is aborted, ignore the error
          if (error instanceof Error && error.name === "AbortError") {
            console.warn("Action aborted, ignoring error", error);
          } else {
            throw error;
          }
        }

        // If the action is successful, dequeue it
        actionsQueue.dequeue();

        // Continue processing the queue
        iterateQueue();
      } catch (error) {
        // If the action fails, log the error and set the status to AwaitingInteraction to continue iterating on the next user interaction

        console.error(
          "Error caught trying to run activation dependent action, will retry on next user interaction",
          error,
        );
        status.value = ActivationQueueStatus.AwaitingInteraction;
      }
    }, 0);
  }

  watchEffect((onCleanup) => {
    const isEmpty = actionsQueue.isEmpty.value;

    if (isEmpty || status.value === ActivationQueueStatus.AwaitingInteraction)
      return;

    iterateQueue();
    onCleanup(() => {
      if (queueTimeout.value) window.clearTimeout(queueTimeout.value);
    });
  });

  watchEffect((onCleanup) => {
    const element = document;

    const listener = async () => {
      isAwaitingInteraction.value = false;
    };

    // Use capture phase: https://stackoverflow.com/a/65332405
    element.addEventListener("click", listener, true);
    onCleanup(() => {
      element.removeEventListener("click", listener, true);
    });
  });

  const definition = {
    status,
    enqueue: actionsQueue.enqueue,
  };

  provideLocal(activationContainerInjectionKey, definition);
  return definition;
}

export function useActivationContainer() {
  const container = injectLocal(activationContainerInjectionKey);
  if (!container) {
    throw new Error(
      "useActivationContainer must be used inside a component that calls useProvideActivationContainer",
    );
  }

  const requiresInteractionForActivation = computed(() => {
    return container.status.value === ActivationQueueStatus.AwaitingInteraction;
  });

  return {
    status: container.status,
    requiresInteractionForActivation,
  };
}

export function useActivationDependentAction<T extends readonly unknown[]>(
  fn: (...T: [...T]) => void,
) {
  const container = injectLocal(activationContainerInjectionKey);
  if (!container) {
    throw new Error(
      "useActivationDependentAction must be used inside a component that calls useProvideActivationContainer",
    );
  }

  const { enqueue } = container;

  function handler(...args: [...T]) {
    enqueue(() => fn(...args));
  }

  return { handler };
}
